import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, signal } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { PalantirButtonComponent, PalantirSkeletonComponent } from '@fieldcontrol/palantir'
import { EquipmentPagedResult, MaintenancePagedResult } from '@generated/sammathnaur.graphql'
import { TranslateModule } from '@ngx-translate/core'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { Subject, fromEvent, merge, takeUntil } from 'rxjs'
import { DetailItemComponent } from './components/detail-item/detail-item.component'
import { DetailPageComponent } from './components/detail-page/detail-page.component'
import { DetailPageOptions } from './components/detail-page/detail-page.types'
import { WarrantyItemComponent } from './components/warranty-item/warranty-item.component'
import { DetailsSectionService } from './details-section.service'
import { EmitEvent } from './details-section.types'
import { LocationsService } from '../../../../core/api/locations/locations.service'
import { LocationsEnvironmentsService } from '../../../../core/api/locations-environments/locations-environments.service'
import { LocationsSectorsService } from '../../../../core/api/locations-sectors/locations-sectors.service'
import { FcMenuComponent } from '../../../../core/components/fc-menu/fc-menu.component'
import { FcMenuOptionComponent } from '../../../../core/components/fc-menu-option/fc-menu-option.component'
import { MAINTENANCE_STATUS_COLORS, MAINTENANCE_STATUS_COLORS_OPACITY_10 } from '../../../../core/constants/maintenance-status-colors.constant'
import { delay } from '../../../../core/others/delay'
import { PipesModule } from '../../../../core/pipes/pipes.module'
import { GlobalEventNameEnum } from '../../../../core/services/global-events/global-events.types'
import { AssetsTreeService } from '../../services/assets-tree.service'
import { TreeViewItemTypeEnum } from '../tree-section/components/tree-view/tree-view.type'

@Component({
    selector: 'app-details-section',
    templateUrl: './details-section.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FcMenuComponent,
        FcMenuOptionComponent,
        PalantirButtonComponent,
        DetailItemComponent,
        MatIconModule,
        DetailPageComponent,
        PalantirSkeletonComponent,
        TranslateModule,
        PipesModule,
        WarrantyItemComponent,
        NgScrollbarModule,
    ]
})
export class DetailsSectionComponent implements OnInit, OnDestroy {
  public treeItemTypeEnum = TreeViewItemTypeEnum

  public destroy$ = new Subject<void>()
  private locationsService = inject(LocationsService)
  public assetsTreeService = inject(AssetsTreeService)
  private changeDetectorRef = inject(ChangeDetectorRef)
  private detailsSectionService = inject(DetailsSectionService)
  private locationsSectorService = inject(LocationsSectorsService)
  private locationsEnvironmentsService = inject(LocationsEnvironmentsService)

  public showPage = signal<boolean>(false)
  public pageOptions = signal<DetailPageOptions | null>(null)

  public isLoading = signal<boolean>(false)
  public details = signal<any>(null)

  public hasEditPermission = signal<boolean>(false)
  public hasEditLocationPermission = signal<boolean>(false)
  public hasCreateEquipmentPermission = signal<boolean>(false)

  public isHoveringEquipmentIndex = signal<number>(-1)
  public isHoveringMaintenanceIndex = signal<number>(-1)
  public equipments = signal<EquipmentPagedResult | null>(null)
  public maintenances = signal<MaintenancePagedResult | null>(null)
  public readonly maintenanceStatusColors = MAINTENANCE_STATUS_COLORS
  public readonly maintenanceStatusColorsOpacity10 = MAINTENANCE_STATUS_COLORS_OPACITY_10

  public async ngOnInit(): Promise<void> {
    await this.load()

    merge(
      this.assetsTreeService.showDetails$,
      fromEvent(window, GlobalEventNameEnum.Updated),
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (result: any) => {
        const details = result?.detail || {}
        if (this.assetsTreeService.selectedItem()) {
          const reloadPage = this.showPage() && details.reloadPage && details.type === this.pageOptions()?.type
          if (reloadPage) {
            this.closePage()
            this.changeDetectorRef.detectChanges()

            if (details.type === 'maintenance') {
              this.openMaintenancesPage()
              return
            }

            this.openEquipmentsPage()
            return
          }

          await this.load()
        }
      })
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  public trackByFn(index: number): number {
    return index
  }

  public closePage(): void {
    this.showPage.set(false)
    this.pageOptions.set(null)
  }

  public close(): void {
    this.assetsTreeService.closeDetails$.next()
  }

  public emitEvent(type: EmitEvent, id?: string): void {
    const item = this.assetsTreeService.selectedItem()
    if (item) {
      this.detailsSectionService.dispatch({ id, item, type })
    }
  }

  public openEquipmentsPage(): void {
    this.detailsSectionService.setEquipmentsFilter(this.assetsTreeService.selectedItem(), 10)
    this.pageOptions.set({
      type: 'equipment',
      item: this.assetsTreeService.selectedItem(),
      service: this.detailsSectionService.equipmentsService
    })

    this.showPage.set(true)
  }

  public openMaintenancesPage(): void {
    this.detailsSectionService.setMaintenancesFilter(this.assetsTreeService.selectedItem(), 10)
    this.pageOptions.set({
      type: 'maintenance',
      item: this.assetsTreeService.selectedItem(),
      service: this.detailsSectionService.maintenancesService
    })

    this.showPage.set(true)
  }

  public async load(): Promise<void> {
    this.closePage()
    this.isLoading.set(true)

    const item = this.assetsTreeService.selectedItem()
    if (!item?.id) {
      return
    }

    let queryService = null
    if (item?.type === TreeViewItemTypeEnum.Equipment) {
      queryService = this.detailsSectionService.equipmentsService
    }

    if (item?.type === TreeViewItemTypeEnum.Location) {
      queryService = this.locationsService
    }

    if (item?.type === TreeViewItemTypeEnum.LocationSector) {
      queryService = this.locationsSectorService
    }

    if (item?.type === TreeViewItemTypeEnum.LocationEnvironment) {
      queryService = this.locationsEnvironmentsService
    }

    const { data } = await queryService?.searchOne(item.id) as Record<string, any>
    this.details.set(data[item.type])

    if (item?.type !== TreeViewItemTypeEnum.Equipment && this.assetsTreeService.hasEquipmentViewPermission()) {
      const equipments = await this.detailsSectionService.loadEquipments(item)
      this.equipments.set(equipments)
    }

    if (this.assetsTreeService.hasMaintenanceViewPermission()) {
      const maintenances = await this.detailsSectionService.loadMaintenances(item)
      this.maintenances.set(maintenances)
    }

    await delay(500)
    this.isLoading.set(false)
  }
}
